import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Card,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { getProjectCohortPath } from '@verantos-dev/core-constants';
import { ArtifactStatusChip } from '@verantos-dev/shared-components';
import { borderRadius, fontWeight } from '@verantos-dev/shared-ui-components/tokens';

import { AddCohortPickerModal } from './AddCohortPickerModal';
import { useSourceCohortSection } from '../../hooks/useSourceCohortSection';
import { useStudyPeriodSection } from '../../hooks/useStudyPeriodSection';
import type { CohortAndStudyPeriodSectionProps } from '../../types';
import { toAssetStatus } from '../../utils/assetStatusLabel';

// Source cohort and study period share one card: the study period is inherited
// from the cohort, so the two are shown together, whether or not a cohort is
// set yet — unselected cohort and undefined study period are placeholder
// states within the same layout, not a separate empty-state treatment.
export const CohortAndStudyPeriodSection = ({
  isLoading = false,
}: CohortAndStudyPeriodSectionProps) => {
  const navigate = useNavigate();
  const {
    projectId,
    sectionLabel,
    addLabel,
    notSelectedText,
    cohort,
    metaLine,
    isLoading: isCohortLoading,
    isPickerOpen,
    onAddCohort,
    onClosePicker,
    isEditDisabled,
    editLockTooltip,
  } = useSourceCohortSection(isLoading);
  const {
    sectionLabel: studyPeriodLabel,
    isSet,
    dateRangeText,
    notSetText,
    isLoading: isStudyPeriodLoading,
  } = useStudyPeriodSection(isLoading);

  const showSkeleton = isCohortLoading || isStudyPeriodLoading;

  const content = showSkeleton ? (
    <Skeleton
      variant="rounded"
      height={80}
      animation="wave"
      sx={{ borderRadius: `${borderRadius.md}px`, flex: 1 }}
      data-testid="analysis-cohort-study-period-loading"
    />
  ) : (
    <>
      <Stack direction="row" alignItems="center" gap={4} sx={{ flex: 1, minWidth: 0 }}>
        {cohort ? (
          <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: fontWeight.semibold }}>
              {sectionLabel}
            </Typography>
            <Stack direction="row" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => navigate(getProjectCohortPath(projectId, cohort.cohortProfileId))}
                sx={{
                  minWidth: 0,
                  maxWidth: '100%',
                  typography: 'body1',
                  fontWeight: fontWeight.medium,
                  color: 'primary.main',
                  textAlign: 'left',
                }}
                data-testid={`analysis-source-cohort-link-${cohort.cohortProfileId}`}
              >
                {cohort.cohortName}
              </Link>
              <ArtifactStatusChip
                status={toAssetStatus(cohort.status)}
                data-testid={`analysis-source-cohort-status-${cohort.cohortProfileId}`}
              />
              {/* span wrapper so the tooltip still shows while disabled */}
              <Tooltip title={editLockTooltip ?? ''} disableHoverListener={!editLockTooltip}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onAddCohort}
                    disabled={isEditDisabled}
                    aria-label={addLabel}
                    data-testid="analysis-source-cohort-add"
                  >
                    <SwapHorizIcon fontSize="small" color="primary" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {metaLine}
            </Typography>
          </Stack>
        ) : (
          <Stack
            spacing={1}
            sx={{ minWidth: 0, flex: 1 }}
            data-testid="analysis-source-cohort-empty"
          >
            <Typography variant="body1" sx={{ fontWeight: fontWeight.semibold }}>
              {sectionLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {notSelectedText}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
      <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

      <Stack direction="row" alignItems="center" gap={4} sx={{ minWidth: 0 }}>
        {isSet ? (
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: fontWeight.semibold }}>
              {studyPeriodLabel}
            </Typography>
            <Typography variant="body1" data-testid="analysis-study-period-card">
              {dateRangeText}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ minWidth: 0 }} data-testid="analysis-study-period-empty">
            <Typography variant="body1" sx={{ fontWeight: fontWeight.semibold }}>
              {studyPeriodLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {notSetText}
            </Typography>
          </Stack>
        )}
      </Stack>
    </>
  );

  const cardSx = {
    p: 4,
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { md: 'stretch' },
    gap: 6,
    minWidth: 0,
  } as const;

  return (
    <>
      <Card variant="outlined" sx={cardSx}>
        {content}
      </Card>
      {isPickerOpen && <AddCohortPickerModal projectId={projectId} onClose={onClosePicker} />}
    </>
  );
};
